import { visibleText } from './expertLive';

// Deterministic mood of each expert during the live debate, derived only from
// the structured data the pipeline already stores (no model is asked for
// emotions): who is writing, and every expert's declared positions on the
// agenda items (agenda -> round-table turns -> outcome).
//
//   speaking   an answer of the expert is being written
//   thinking   the expert's answer has started but nothing is written yet
//   conflict   the expert stands alone on an item still open, persistent, or
//              decided by the majority against it
//   aligned    the expert just joined another's position, or has no isolated
//              position left once the agenda exists
//   listening  otherwise

export const MOOD_LABELS = {
  speaking: 'Prend la parole',
  thinking: 'Réfléchit',
  conflict: 'En désaccord',
  aligned: 'Aligné',
  listening: 'Écoute',
};

// Mascot animation state for each mood (Mascotte / MascotteSpine vocabulary).
export const MOOD_MASCOT_STATE = {
  speaking: 'talk',
  thinking: 'think',
  conflict: 'surprise',
  aligned: 'smile',
  listening: 'idle',
};

const KIND_LABELS = {
  regime: 'régime de marché', rates_outlook: 'trajectoire des taux', equity_exposure: 'exposition actions',
  cash: 'liquidités', action: 'action', weight: 'pondération', horizon: 'horizon', scenario: 'scénario de résultats',
  watchlist: 'watchlist', proposition: 'hypothèse',
};

const itemLabel = (it) => (it.kind === 'proposition' ? it.subject : `${it.subject === 'macro' ? 'Macro' : it.subject} — ${KIND_LABELS[it.kind] || it.kind}`);

const WATCH_RANK = { none: 0, low: 1, medium: 2, high: 3 };

// Same tolerances as the backend convergence matrix (expert_convergence.go).
export const sameStance = (kind, a, b) => {
  if (!a || !b) return false;
  if (kind === 'weight' || kind === 'cash') return Math.abs(Number(a) - Number(b)) <= (kind === 'cash' ? 5 : 3);
  if (kind === 'watchlist') return Math.abs((WATCH_RANK[a] ?? 0) - (WATCH_RANK[b] ?? 0)) <= 1;
  return a === b;
};

const asItems = (data) => (Array.isArray(data) ? data.map((it) => ({ ...it, stances: { ...(it.stances || {}) } })) : null);

// Experts in order of first appearance, with the mascot they used.
export function panelMembers(entries, streams) {
  const members = new Map();
  [...entries, ...streams].forEach((x) => {
    if (!x.expert_name || x.expert_name === 'Panel' || members.has(x.expert_name)) return;
    members.set(x.expert_name, x.mascot_variant || '');
  });
  return [...members].map(([name, mascot]) => ({ name, mascot }));
}

export function computeMoods(entries, streams) {
  const names = panelMembers(entries, streams).map((m) => m.name);
  const moods = Object.fromEntries(names.map((n) => [n, { mood: 'listening', reason: '' }]));

  // Replay the declared positions: agenda, then every round-table turn; the
  // outcome, when present, is the authoritative final state.
  let items = null;
  const lastChange = {}; // expert -> item joined on its latest turn
  entries.forEach((e) => {
    if (e.phase === 'agenda') items = asItems(e.data);
    if (e.phase === 'outcome') items = asItems(e.data) || items;
    if (e.phase !== 'roundtable' || !items || !e.data || typeof e.data !== 'object') return;
    lastChange[e.expert_name] = null;
    Object.entries(e.data).forEach(([id, stance]) => {
      const it = items.find((x) => x.id === id);
      if (!it) return;
      const before = it.stances[e.expert_name];
      it.stances[e.expert_name] = stance;
      const joined = names.some((o) => o !== e.expert_name && sameStance(it.kind, stance, it.stances[o]));
      if (before !== stance && joined) lastChange[e.expert_name] = it;
    });
  });

  if (items) {
    const disputed = items.filter((it) => ['open', 'persistent', 'majority'].includes(it.status));
    names.forEach((n) => {
      const alone = disputed.find((it) => it.stances[n]
        && names.filter((o) => o !== n && it.stances[o]).length > 0
        && !names.some((o) => o !== n && sameStance(it.kind, it.stances[n], it.stances[o])));
      if (alone) moods[n] = { mood: 'conflict', reason: `Isolé sur ${itemLabel(alone)}` };
      else if (lastChange[n]) moods[n] = { mood: 'aligned', reason: `A rejoint une position sur ${itemLabel(lastChange[n])}` };
      else moods[n] = { mood: 'aligned', reason: 'Aucune position isolée' };
    });
  }

  streams.forEach((s) => {
    if (s.done || !moods[s.expert_name]) return;
    moods[s.expert_name] = visibleText(s.text || '').trim()
      ? { mood: 'speaking', reason: 'Écrit en direct' }
      : { mood: 'thinking', reason: 'Prépare son intervention' };
  });
  return moods;
}

// Cheap signature of what can change a mood, so moods (and the mascots) are
// not recomputed or re-rendered on every text fragment.
export const moodSignature = (entries, streams) => `${entries.length}|${streams
  .map((s) => `${s.key}:${s.done ? 1 : 0}:${visibleText(s.text || '').trim() ? 1 : 0}`)
  .join(',')}`;
